import { useRouter, useSearchParams } from "next/navigation";
import { memo, useEffect, useMemo, useState, type JSX } from "react";
import { useDispatch, useSelector } from "react-redux";
import { InlineInfoBox, LeftArrowIcon } from "staysure-component-library";

import { getQuoteReviewSummaryEditSection } from "@/app/common-utils/edit-quote-review-summary-flow.utils";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import BtnLoadingSvg from "@/components/atoms/btn-loading-svg/btn-loading-svg";
import LabelButtonWrapper, {
  IconButtonWrapper,
} from "@/components/atoms/button-wrapper/button-wrapper";
import RichText from "@/components/atoms/rich-text/rich-text";
import RenewalMTAQuoteCreationModal from "@/components/organisms/renewal-mta-consent-popup/renewal-mta-consent-popup";
import withApplication from "@/components/with-application";
import {
  FinishEdit,
  MedicalEdit,
  OptimizelyExperimentsEnum,
  OrganizerEdit,
  Quote,
  RenewalPage,
  TravellerEdit,
} from "@/config/app-constant";
import {
  APPLICATION,
  BRAND_STAYSURE,
  PRODUCT,
  VARIANT_FLOW,
} from "@/constants";
import useAddMedicalConditions from "@/hooks/api/medical/use-medical-conditions";
import useFinishButtonClickEvent from "@/hooks/gtm/medical-dashboard/finish-button-click-handler";
import useMediaQuery from "@/hooks/use-media-query/use-media-query";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { useLoading } from "@/providers/loading-provider";
import { RootState } from "@/store/root-reducer";
import { setApiError } from "@/store/slices/errors/api-error.slice";
import { updateQuote } from "@/store/slices/quote/quote.slice";
import { storageAvailable } from "@/utils/common.utils";
import { apiErrorMappings } from "@/utils/errors/api-error-map";

import Styles from "./medical-dashboard-section.module.css";

interface Props {
  dashBordTitle: string;
  supportingText: string;
  infoBoxContent: string;
  ctaButtonText: string;
  mediclConditionText: string;
  addBtnText: string;
  editBtnText: string;
  finishEditingBtnText: string;
  noMedicalTitle?: string;
  noMedicalInfoBoxContent?: string;
  application?: "quote" | "renewal";
}

function MedicalDashboardSection({
  dashBordTitle,
  supportingText,
  infoBoxContent,
  ctaButtonText,
  mediclConditionText,
  addBtnText,
  editBtnText,
  finishEditingBtnText,
  noMedicalTitle,
  noMedicalInfoBoxContent = "",
  application,
}: Readonly<Props>): JSX.Element {
  const isDesktop = useMediaQuery("(min-width: 600px)");
  const router = useRouter();
  const searchParams = useSearchParams();
  const editPage =
    searchParams.get(MedicalEdit) ||
    searchParams.get(TravellerEdit) ||
    searchParams.get(OrganizerEdit);
  const { hideLoading, showLoading } = useLoading();

  const dispatch = useDispatch();
  const { attributes, version, referenceId } = useSelector(
    (state: RootState) => state.quote
  );
  const [selectedTravellerId, setSelectedTravellerId] = useState<string | null>(
    null
  );

  const [mainBtnLoading, setMainBtnLoading] = useState<boolean>(false);
  const [btnLoading, setBtnLoading] = useState<boolean>(false);
  const [getUserTravelingConcent, setGetUserTravelingConcent] = useState(false);

  const travellers = attributes.travellerDetails?.traveller;

  const { current, wizard } = useWizard();
  const { productSpecialFeatures } = wizard;
  const showCustomMedicalErrorModal =
    productSpecialFeatures?.showCustomErrorModalForMedicalServiceUnavailability;

  // Indicator to trigger hook flash logic
  const [triggerHookFlash, setTriggerHookFlash] = useState<boolean>(false);
  const { t } = useWizardTranslation();

  const agreedTreatments =
    attributes?.travellerDetails?.gateKeeperAnswers?.agreedTreatments;
  const isMedicalNoAbTest = useMemo(
    () =>
      globalThis.window !== undefined &&
      APPLICATION === Quote &&
      PRODUCT === BRAND_STAYSURE &&
      agreedTreatments === false &&
      localStorage.getItem(
        OptimizelyExperimentsEnum.MEDICAL_NO_TO_DASHBOARD
      ) === VARIANT_FLOW,
    [agreedTreatments]
  );

  const getTravellerMedical = () => {
    let hasMedicalCondition = false;
    if (travellers) {
      hasMedicalCondition = travellers.some(
        (traveller) => traveller.medical !== undefined
      );
    }
    return hasMedicalCondition;
  };

  const is5001Error = (error: any) => {
    return (
      error?.response?.status === 500 &&
      (error?.response?.data as any)?.errors?.[0]?.code === 5001 &&
      showCustomMedicalErrorModal
    );
  };

  // Medical API
  const { patchMedicalConditions, data, loading, error } =
    useAddMedicalConditions();

  // Handle the add medical response
  useEffect(() => {
    const handleEffect = async () => {
      if (data !== null && !error) {
        const veriskUri = `${data.attributes.thirdPartyRedirection?.response.redirectUrl}&flowId=1.5`;
        dispatch(updateQuote(data));

        const hookFlashMedical = JSON.stringify({
          hookFlash: false,
          veriskUri,
        });
        if (storageAvailable()) {
          sessionStorage.setItem("veriskObj", hookFlashMedical);
        }

        // encode related traveller id from thirdparty response
        const encodedTravellerId = encodeURIComponent(
          data.attributes.thirdPartyRedirection?.response
            ?.travellerEncryptedId || ""
        );
        // encode related hash from thirdparty response
        const hashId = encodeURIComponent(
          data.attributes.thirdPartyRedirection?.response?.hash || ""
        );

        const queryParams = new URLSearchParams({
          travellerId: encodedTravellerId || "",
          hash: hashId || "",
        });

        // Preserve specific edit flow parameters
        if (searchParams.get(MedicalEdit)) {
          queryParams.append("medicalEdit", "true");
        }
        if (searchParams.get(TravellerEdit)) {
          queryParams.append("travellerEdit", "true");
        }
        if (searchParams.get(OrganizerEdit)) {
          queryParams.append("organizerEdit", "true");
        }
        if (data.attributes.thirdPartyRedirection?.response?.showWarning) {
          queryParams.append("showWarning", "true");
        }

        if (encodedTravellerId && hashId) {
          window.open(
            `/${t(
              "medical_screen_path",
              "medical-screen"
            )}?${queryParams.toString()}`,
            "_self"
          );
          // window.open(`/medical-screen?${queryParams.toString()}`, "_self");
        } else {
          setBtnLoading(false);
        }

        hideLoading();
        // &hashCode=${data.attributes?.hashCode}
        // window.open(veriskUri, "_self");
      }
      if (!loading && error !== null) {
        const encodedTravellerId = selectedTravellerId;
        // encode related hash from thirdparty response

        const queryParams = new URLSearchParams({
          travellerId: encodedTravellerId || "",
        });

        if (error.response?.status === 401) {
          router.push("/");
        } else if (is5001Error(error)) {
          queryParams.append("veriskUnavailable", "true");

          // Preserve specific edit flow parameters
          if (searchParams.get(MedicalEdit)) {
            queryParams.append("medicalEdit", "true");
          }
          if (searchParams.get(TravellerEdit)) {
            queryParams.append("travellerEdit", "true");
          }
          if (searchParams.get(OrganizerEdit)) {
            queryParams.append("organizerEdit", "true");
          }

          window.open(
            `/${t(
              "medical_screen_path",
              "medical-screen"
            )}?${queryParams.toString()}`,
            "_self"
          );
        } else {
          dispatch(setApiError(await apiErrorMappings(error)));
          hideLoading();
          setBtnLoading(false);
        }
      }
    };
    handleEffect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, dispatch, loading, router]);

  useEffect(() => {
    const handleEffect = async () => {
      if (error) {
        setSelectedTravellerId(null);
        // Only show error modal for non-5001 errors
        // 5001 errors are handled by redirect to medical-screen
        if (!is5001Error(error)) {
          dispatch(
            setApiError({
              ...(await apiErrorMappings(error)),
              ctaPrimaryBtnLabel: t("try_again", "Try Again"),
              multiButton: true,
            })
          );
        }
      }
    };
    handleEffect();
  }, [error, dispatch, router, t]);

  const handleAddBtn = async (travellerId: string, isEdit = false) => {
    const isPageEdit = !!editPage;
    setBtnLoading(true);
    showLoading();
    setSelectedTravellerId(travellerId);
    await patchMedicalConditions({
      travellerId,
      referenceId,
      version,
      isEdit,
      isPageEdit,
    });
  };

  const handleBackButtonClick = () => {
    router.push(current.prev ?? "");
  };

  const handleMainButtonClick = () => {
    if (
      application === RenewalPage &&
      attributes.mtaMedicalConsent?.validForMtaCreation
    ) {
      setGetUserTravelingConcent(true);
    } else {
      setMainBtnLoading(true);

      if (editPage) {
        // Check if edit originated from quote-review page
        const editSection = getQuoteReviewSummaryEditSection();
        if (editSection) {
          // Navigate back to quote-review page
          router.push(
            current.edges.cancelQuoteReviewSummaryEdit ?? "/quote-review"
          );
          return;
        }

        router.push(current.edges.editFlowNext ?? "");
      } else if (application === RenewalPage) {
        router.push("/");
      } else {
        router.push(current.next ?? "");
      }
    }

    // Set hook flash trigger to true
    setTriggerHookFlash(true);
  };

  // Invoke hook flash logic for Dashboard FINISH button click event
  useFinishButtonClickEvent({
    tripDetails: attributes?.tripDetails,
    travelerList: attributes?.travellerDetails?.traveller,
    ready: triggerHookFlash,
  });

  const renderBackButton = () => {
    return (
      <IconButtonWrapper
        data-testid="medical-dashboard-back-button"
        id="medical-dashboard-back-button"
        btnType="icon"
        onClick={handleBackButtonClick}
        semanticColor={isDesktop ? "Dark Background" : "Light Background"}
        icon={<LeftArrowIcon />}
        hierarchy="secondary"
        className={` z-[2] relative min-w-[56px] w-[56px] min-h-[56px] h-[56px] flex justify-center items-center leading-[18px]`}
      />
    );
  };

  const renderSvgPlus = () => {
    return (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g id="Plus">
          <path
            id="Vector"
            d="M12 5V19M5 12H19"
            stroke="#306582"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      </svg>
    );
  };

  const travelerConcentModal = useMemo(() => {
    return (
      <RenewalMTAQuoteCreationModal
        isOpen={getUserTravelingConcent}
        currentUsagePage="medical-dashboard"
      />
    );
  }, [getUserTravelingConcent]);

  return (
    <div data-testid="travel-eligibility">
      <div className="bg-dark-surface-accent-1-primary xs:max-w-[280px] w-full rounded-b-2xl flex flex-col items-center mx-auto p-6 py-8 md:py-0 md:my-8">
        <div className="w-full text-center text-dark-text-on-primary">
          <h1 className="md:max-w-[590px] mx-auto mb-spacing-xs font-header-font md:text-[39px] md:leading-[44.85px] leading-[29.9px] text-[26px] text-center">
            {application === RenewalPage && !getTravellerMedical()
              ? noMedicalTitle
              : dashBordTitle}
          </h1>
          <p className="text-xs leading-[27px] sm:px-[17px] text-surface-neutral-option-1">
            {supportingText}
          </p>
        </div>
      </div>
      {application !== RenewalPage && !getTravellerMedical() && (
        <InlineInfoBox
          content={<RichText richText={infoBoxContent} />}
          className="md:max-w-[500px] bg-surface-info-2 text-neutral-900"
          style={{
            width: "100%",
            margin: "16px auto 0 auto",
            borderColor: "#006BD6",
            padding: "8px 16px 8px 16px",
            borderRadius: "8px",
            fontSize: "16px",
            lineHeight: "24px",
          }}
        />
      )}

      {application === RenewalPage && travelerConcentModal}

      {application === RenewalPage && (
        <InlineInfoBox
          content={
            <RichText
              richText={
                !getTravellerMedical()
                  ? noMedicalInfoBoxContent
                  : infoBoxContent
              }
            />
          }
          className="md:max-w-[500px] bg-surface-info-2 text-neutral-900"
          style={{
            width: "100%",
            margin: "16px auto 0 auto",
            borderColor: "#006BD6",
            padding: "8px 16px 8px 16px",
            borderRadius: "8px",
            fontSize: "16px",
            lineHeight: "24px",
          }}
        />
      )}

      {travellers?.map((person) => {
        return (
          // Details should be display in a card component
          <div
            className={`${Styles.card} mt-4 md:mt-6 md:max-w-[500px] mx-auto`}
            key={person.encryptedId}
          >
            <div className={`${Styles.trav_details} `}>
              <span className={`${Styles.trav_name} `}>
                {`${person.firstName} ${person.lastName}`}
              </span>
              <span className={`${Styles.trav_age} `}>{`${t("age", "Age")} ${
                person.age
              }`}</span>
            </div>

            <div className={`${Styles.trav_med_conditions} `}>
              {person.medical ? (
                <ul>
                  {person.medical?.condition
                    .split("|")
                    .map((condition, index) => {
                      return <li key={index}>{condition}</li>;
                    })}
                </ul>
              ) : (
                // <span>{person.medical.condition.split("|").join(", ")}</span>
                <span>{mediclConditionText}</span>
              )}
            </div>
            <div className="w-fit">
              <LabelButtonWrapper
                data-testid={`medical-dashboard-add-button-${person.encryptedId}`}
                id={`medical-dashboard-add-button-${person.encryptedId}`}
                label={
                  selectedTravellerId === person.encryptedId
                    ? "Redirecting..."
                    : `${person.medical ? editBtnText : addBtnText}`
                }
                hierarchy="secondary"
                isLeftIcon
                className="!h-[40px] px-spacing-m whitespace-nowrap"
                leftIcon={renderSvgPlus()}
                isDisabled={loading}
                onClick={() =>
                  handleAddBtn(person.encryptedId ?? "", !!person.medical)
                }
                isLoading={
                  selectedTravellerId === person.encryptedId
                    ? btnLoading
                    : false
                }
                loadingIcon={btnLoading ? <BtnLoadingSvg /> : <div />}
              />
            </div>
          </div>
        );
      })}

      <div
        className={`flex justify-center ${Styles.button_Wrapper} gap-spacing-m`}
      >
        {!editPage && renderBackButton()}
        <LabelButtonWrapper
          data-testid="medical-dashboard-submit-button"
          id="medical-dashboard-submit-button"
          isDisabled={
            (editPage && !isMedicalNoAbTest) || isMedicalNoAbTest
              ? false
              : !getTravellerMedical()
          }
          label={editPage ? finishEditingBtnText || FinishEdit : ctaButtonText}
          hierarchy="primary"
          className={`${
            !editPage
              ? "lg:w-[342px] md:w-[342px] sm:w-[244px]"
              : "lg:!w-[414px] md:!w-[400px] sm:!w-[312px]"
          } flex flex-col text-text-on-primary  justify-center items-center gap-grid-system-space-050 z-[2] relative px-spacing-l py-spacing-s font-body-font text-xs leading-[18px] font-semibold h-[56px]`}
          type="button"
          onClick={handleMainButtonClick}
          isLoading={mainBtnLoading}
          loadingIcon={mainBtnLoading ? <BtnLoadingSvg /> : <div />}
        />
      </div>
    </div>
  );
}

export default memo(withApplication(MedicalDashboardSection));
